import DOMPurify from "dompurify"

export function reactor(controller) {
  attach(controller, new Reactor(controller))
}

// The morph hook alone, for a controller that binds nothing through data attributes.
export function onMorph(controller) {
  attach(controller, new MorphHook(controller))
}

// Stimulus reuses a controller when its element is re-inserted, so disconnect is wrapped once.
function attach(controller, instance) {
  if (!("reactorInstance" in controller)) {
    const disconnect = controller.disconnect.bind(controller)

    controller.disconnect = () => {
      controller.reactorInstance.disconnect()
      disconnect()
    }
  }

  controller.reactorInstance = instance
}

class MorphHook {
  constructor(controller) {
    this.controller = controller
    document.addEventListener("turbo:morph", this.morph)
    document.addEventListener("turbo:frame-render", this.frameRender)
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.morph)
    document.removeEventListener("turbo:frame-render", this.frameRender)
  }

  morph = (event) => this.controller.morph(event)

  frameRender = (event) => {
    if (event.target.contains(this.controller.element)) this.controller.morph(event)
  }
}

class Reactor {
  // Each binding and the method that applies it to one element, in the order update() runs them.
  static BINDINGS = {
    model: "updateModelElement",
    text: "updateTextElement",
    html: "updateHTMLElement",
    class: "updateClassElement",
    show: "updateShowElement",
    hide: "updateHideElement",
    if: "updateIfElement",
    unless: "updateUnlessElement",
  }

  constructor(controller) {
    this.controller             = controller
    this.setControllerValues    = {}
    this.boundModelElements     = new Set()
    this.modelInputEventHandler = this.modelInputEventHandler.bind(this)
    this.morphHandler           = this.morphHandler.bind(this)
    this.frameRenderHandler     = this.frameRenderHandler.bind(this)

    this.update()
    this.observe()
    this.bind()
  }

  // data-<identifier>-<name>
  attribute(name) {
    return `data-${this.controller.scope.identifier}-${name}`
  }

  // Calls fn for the controller element, when it carries the binding, and for every descendant that does.
  eachBound(kind, fn) {
    const attribute = this.attribute(kind)
    const element   = this.controller.element

    if (element.hasAttribute(attribute))
      fn.call(this, element)

    element.querySelectorAll(`[${attribute}]`).forEach(bound => fn.call(this, bound))
  }

  disconnect() {
    this.observer.disconnect()

    document.removeEventListener("turbo:morph", this.morphHandler)
    document.removeEventListener("turbo:frame-render", this.frameRenderHandler)

    for (const element of this.boundModelElements) {
      element.removeEventListener("input", this.modelInputEventHandler)
    }
    this.boundModelElements.clear()

    this.controller.element
      .querySelectorAll(`[${this.attribute("if-dst")}]`)
      .forEach(clone => clone.remove())
  }

  bind() {
    document.addEventListener("turbo:morph", this.morphHandler)
    document.addEventListener("turbo:frame-render", this.frameRenderHandler)
  }

  frameRenderHandler(event) {
    if (!event.target.contains(this.controller.element)) return
    this.morphHandler(event)
  }

  morphHandler(event) {
    this.setControllerValues = {}
    this.update()
    if (typeof this.controller.morph === "function")
      this.controller.morph.call(this.controller, event)
  }

  observe() {
    const watched = new Set(Object.keys(Reactor.BINDINGS).map(kind => this.attribute(kind)))

    // A binding taken off an element leaves what it last wrote, so that is cleared first.
    const resets = {
      [this.attribute("text")]: this.setElementText,
      [this.attribute("html")]: this.setElementHTML,
      [this.attribute("show")]: this.setElementDisplay,
      [this.attribute("hide")]: this.setElementDisplay,
      [this.attribute("class")]: this.setElementClass,
    }

    this.observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.target === this.controller.element) {
          this.update()
          break
        }

        if (watched.has(mutation.attributeName)) {
          const reset = resets[mutation.attributeName]

          if (reset && !mutation.target.hasAttribute(mutation.attributeName))
            reset.call(this, mutation.target)

          this.update()
          break
        }

        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.update()
            break
          }
        }
      }
    })

    this.observer.observe(
      this.controller.element,
      {
        childList: true,
        subtree: true,
        attributes: true
      }
    )
  }

  update() {
    this.eachBound("model", this.warnMissingValueDefinition)
    this.eachBound("model", this.updateControllerValueWithElementValue)

    for (const [kind, method] of Object.entries(Reactor.BINDINGS))
      this.eachBound(kind, this[method])

    this.eachBound("model", this.updateModelElementEventListener)
  }

  warnMissingValueDefinition(element) {
    const property = element.getAttribute(this.attribute("model"))

    if (this.controller.constructor.values[property] === undefined)
      console.warn(`[Reactor] Missing value definition: ${property} (${property}Value). ` +
                   `Define it on the ${this.controller.identifier} Stimulus controller. ` +
                   `For example: static values = { ${property}: String }`)
  }

  updateControllerValueWithElementValue(element) {
    const property = element.getAttribute(this.attribute("model"))
    const value    = this.getElementValue(element)

    if (this.isControllerValueUnset(property)) {
      this.setControllerValue(property, value)
      this.runChangedCallback(property, value)
    }
  }

  runChangedCallback(property, value) {
    if (this.controller.constructor.values[property] !== undefined)
      return

    const fn = this.controller[`${property}ValueChanged`]

    if (typeof fn === "function")
      fn.call(this.controller, value)
  }

  updateModelElementEventListener(element) {
    element.removeEventListener("input", this.modelInputEventHandler)
    this.boundModelElements.delete(element)

    if (element.hasAttribute(this.attribute("model"))) {
      element.addEventListener("input", this.modelInputEventHandler)
      this.boundModelElements.add(element)
    }
  }

  modelInputEventHandler (event) {
    const property = event.target.getAttribute(this.attribute("model"))
    const value    = this.getElementValue(event.target)

    this.setControllerValue(property, value)
    this.runChangedCallback(property, value)
    this.update()
  }

  updateModelElement(element) {
    const property = element.getAttribute(this.attribute("model"))
    const value    = this.getControllerValue(property)

    if (value === undefined || this.isNaNType(value))
      this.setElementValue(element)
    else
      this.setElementValue(element, value)
  }

  setElementValue(element, value = "") {
    if (element.value !== value)
      element.value = value
  }

  updateTextElement(element) {
    const property = element.getAttribute(this.attribute("text"))
    const value    = this.getControllerValue(property)

    if (this.isRenderable(value))
      this.setElementText(element, value)
    else
      this.setElementText(element)
  }

  setElementText(element, value = "") {
    if (element.innerText !== value)
      element.innerText = value
  }

  updateHTMLElement(element) {
    const property = element.getAttribute(this.attribute("html"))
    const value    = this.getControllerValue(property)

    if (typeof value === "string")
      this.setElementHTML(element, value)
    else
      this.setElementHTML(element)
  }

  setElementHTML(element, value = "") {
    const html = DOMPurify.sanitize(value, {USE_PROFILES: {html: true}})

    if (element.innerHTML !== html)
      element.innerHTML = html
  }

  updateClassElement(element) {
    const staticClass = this.attribute("static-class")
    const property    = element.getAttribute(this.attribute("class"))
    const value       = this.getControllerValue(property)

    if (!element.hasAttribute(staticClass))
      element.setAttribute(staticClass, element.getAttribute("class") || "")

    if (Array.isArray(value))
      this.setElementClass(element, value)
    else if (typeof value === "string")
      this.setElementClass(element, [value])
    else
      this.setElementClass(element)
  }

  setElementClass(element, list = []) {
    element.className = element.getAttribute(this.attribute("static-class"))
    list.forEach(className => element.classList.add(className))
  }

  updateShowElement(element) {
    const property = element.getAttribute(this.attribute("show"))
    const value    = this.getControllerValue(property)

    if (property === null || this.isVisible(value))
      this.setElementDisplay(element)
    else
      this.setElementDisplay(element, "none")
  }

  updateHideElement(element) {
    const property = element.getAttribute(this.attribute("hide"))
    const value    = this.getControllerValue(property)

    if (property === null || !this.isVisible(value))
      this.setElementDisplay(element)
    else
      this.setElementDisplay(element, "none")
  }

  setElementDisplay(element, value = "") {
    if (element.style.display !== value)
      element.style.display = value
  }

  updateIfElement(element) {
    this.updateConditional(element, "if", true)
  }

  updateUnlessElement(element) {
    this.updateConditional(element, "unless", false)
  }

  // Inserts the template's one root node while the value's visibility equals wantVisible, and removes it otherwise.
  updateConditional(element, kind, wantVisible) {
    const attribute = this.attribute(kind)

    if (element.tagName !== "TEMPLATE")
      return console.error(`[Reactor] The ${attribute} attribute must be used on `,
                           `<template> elements.`, element)

    for (const inner of [this.attribute("text"), this.attribute("html")]) {
      if (element.hasAttribute(inner))
        return console.error(`[Reactor] The <template> element cannot have a ${inner} ` +
                             `attribute. Suggestion: Define a <div> with ${inner} ` +
                             `inside the <template>.`, element)
    }

    const source      = this.attribute("if-src")
    const destination = this.attribute("if-dst")

    if (!element.hasAttribute(source))
      element.setAttribute(source, this.nextIfSrcAttribute())

    const value      = this.getControllerValue(element.getAttribute(attribute))
    const identifier = element.getAttribute(source)
    const clone      = document.querySelector(`[${destination}="${identifier}"]`)
    const wanted     = this.isVisible(value) === wantVisible

    if (wanted && clone === null) {
      const fragment = element.content.cloneNode(true)

      if (fragment.childElementCount !== 1)
        return console.error(`[Reactor] The <template> element requires exactly 1 root node, ` +
                             `but has ${fragment.childElementCount}. Element:`, element, `Clone:`, fragment)

      fragment.firstElementChild.setAttribute(destination, identifier)
      element.parentNode.insertBefore(fragment, element.nextSibling)
      this.update()
    } else if (!wanted && clone !== null) {
      clone.remove()
    }
  }

  getElementValue(element) {
    switch (element.type) {
    case "checkbox":
      return element.checked
    default:
      return element.value
    }
  }

  getControllerValue(property) {
    if (typeof property !== "string")
      return null
    else if (typeof this.controller[property] === "function")
      return this.controller[property].call(this.controller)
    else
      return this.controller[`${property}Value`]
  }

  setControllerValue(property, value) {
    const key = `${property}Value`

    this.controller[key]          = value
    this.setControllerValues[key] = true
  }

  isControllerValueUnset(property) {
    return this.setControllerValues[`${property}Value`] !== true
  }

  static RENDERABLE = {"number": true, "string": true}

  isRenderable(value) {
    return this.constructor.RENDERABLE[(typeof value)]
  }

  // false, "", null, undefined, and NaN hide. Everything else shows, 0 and "0" included.
  isVisible(value) {
    return !(value === false || value === "" || value === null || value === undefined || Number.isNaN(value))
  }

  static ifSrcAttributeIdentifier = 0

  nextIfSrcAttribute() {
    return this.constructor.ifSrcAttributeIdentifier += 1
  }

  isNaNType(value) {
    return typeof value === "number" && isNaN(value)
  }
}
